import React, { useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  Card,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from "@mui/material";
import {
  useGetMessageQuery,
  useDeleteMessageMutation,
  useMarkMessageAsReadMutation,
} from "../../../Api/attoDeskApi";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DraftsIcon from "@mui/icons-material/Drafts";
import CreateIcon from "@mui/icons-material/Create";
import Pagination from "../../../Components/Pagination";
import useIsMobile from "../../../Core/hook/useIsMobile";
import dayjs from "dayjs";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const isMobileRes = useIsMobile();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [folder, setFolder] = useState<"inbox" | "sent">("inbox");

  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);

  const { data, isLoading, isError } = useGetMessageQuery({ folder });

  const [deleteMessage] = useDeleteMessageMutation();
  const [markAsRead] = useMarkMessageAsReadMutation();

  const messages: any[] = useMemo(() => Array.isArray(data?.data) ? (data?.data as any[]) : [], [data]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteMessage(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeletePopup(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the message");
    }
  };

  const handleMessageClick = async (message: any) => {
    if (folder === "inbox" && !message.isRead) {
      try {
        await markAsRead({ messageId: message.id }).unwrap();
      } catch (error) {
        // Silent fail for mark as read
      }
    }
    navigate(`/message/thread/${message.threadId || message.id}`);
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={2}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={6} md={6} sm={6} xs={6}>
          <Typography className={classes.cardTitle}>Messages</Typography>
        </Grid>
        <Grid item lg={6} md={6} sm={6} xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            className={classes.addNewButton}
            startIcon={<CreateIcon />}
            onClick={() => navigate("/message/create")}
          >
            Compose
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item>
          <ToggleButtonGroup
            value={folder}
            exclusive
            onChange={(e, val) => {
              if (val) {
                setFolder(val);
                setPage(0);
              }
            }}
            size="small"
          >
            <ToggleButton value="inbox">Inbox</ToggleButton>
            <ToggleButton value="sent">Sent</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader} sx={{ width: 30 }}></TableCell>
                <TableCell className={classes.tablheader}><strong>Subject</strong></TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>{folder === "inbox" ? "From" : "To"}</strong>
                </TableCell>
                {!isMobileRes && (
                  <TableCell className={classes.tablheader}><strong>Date</strong></TableCell>
                )}
                <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : isError || messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching messages" : `No messages in ${folder}`}
                  </TableCell>
                </TableRow>
              ) : (
                messages
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any) => (
                    <TableRow
                      key={row.id}
                      className={classes.tableRowHover}
                      sx={{
                        cursor: "pointer",
                        fontWeight: row.isRead === false ? "bold" : "normal",
                        backgroundColor: row.isRead === false ? "action.hover" : "transparent",
                      }}
                      onClick={() => handleMessageClick(row)}
                    >
                      <TableCell className={classes.tablCell}>
                        {row.isRead === false ? (
                          <MailOutlineIcon fontSize="small" color="primary" />
                        ) : (
                          <DraftsIcon fontSize="small" color="disabled" />
                        )}
                      </TableCell>
                      <TableCell
                        className={classes.tablCell}
                        sx={{ fontWeight: row.isRead === false ? "bold" : "normal" }}
                      >
                        {row.subject || "(No Subject)"}
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        {folder === "inbox"
                          ? row.senderName || row.fromName || ""
                          : row.recipientName || row.toName || ""}
                      </TableCell>
                      {!isMobileRes && (
                        <TableCell className={classes.tablCell}>
                          {row.createdAt ? dayjs(row.createdAt).format("YYYY-MM-DD HH:mm") : ""}
                        </TableCell>
                      )}
                      <TableCell className={classes.tablCell}>
                        <Grid container spacing={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Grid item>
                            <Button
                              className={classes.deleteButtonList}
                              onClick={(e) => {
                                e.stopPropagation();
                                setItemToDelete(row);
                                setOpenDeletePopup(true);
                              }}
                            >
                              <DeleteOutlineSharpIcon className={classes.editListIcon} />
                            </Button>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <Grid spacing={5} p={"18px"} py={4} justifyContent={"end"} container textAlign={"center"} alignContent={"center"} alignItems={"center"}>
            <Grid sx={{ textAlign: "end", justifyContent: "flex-end", alignItems: "flex-end" }}>
              <Pagination count={messages.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
            </Grid>
          </Grid>
        </TableContainer>
      </Grid>

      {itemToDelete && (
        <DeletePopup
          open={openDeletePopup}
          handleCloseDelete={() => { setItemToDelete(null); setOpenDeletePopup(false); }}
          onConfirm={async () => { await handleDelete(itemToDelete.id.toString()); }}
          title="Delete Message"
          content={`Are you sure you want to delete this message?`}
        />
      )}
    </Card>
  );
};

export default Index;
